/**
 * Lot PL du chantier 0.5.x — niveau Plateforme (super-administrateur seul).
 *
 * - Continuité de la plateforme DoIt (SRF-PLT-005, DEC-054) : `PlatformContinuityController`.
 *   La passerelle LIT le journal `backup_runs.jsonl` et le registre de rétention, ENREGISTRE les
 *   demandes et rend la commande EXACTE à lancer sur l'hôte ; elle n'exécute jamais rien.
 * - Accès exceptionnels (SRF-PLT-009, DEC-051) : `ExceptionalAccessController`. La base juge la
 *   fenêtre (expiration, révocation) et borne la lecture à l'organisation visée.
 * - Palier et relevé d'une organisation (SRF-PLT-003) : `OrganisationPlanController`.
 */
import { getToken } from '../auth/session'
import { ApiError, BASE, httpMessage, request } from './http'

// --------------------------------------------------------------------------- continuité

export interface ComposantSauvegarde {
  component: string
  status: string | null
  bytes: number | null
  sha256: string | null
  error: string | null
}

export interface ResumeExecution {
  kind?: string
  status?: string
  restore_status?: string
  finished_at?: string
  restore_finished_at?: string
  restore_target?: string
  target_container?: string
  backup_id?: string
}

export interface SauvegardeDoIt {
  backup_id: string
  output_dir: string | null
  label: string | null
  status: string | null
  started_at: string | null
  finished_at: string | null
  duration_seconds: number | null
  scope: string
  request_id: string | null
  components: ComposantSauvegarde[]
  total_bytes: number
  fingerprint: string | null
  retention: { legal_hold: boolean; retention_expires_at: string | null; rotated_at: string | null } | null
  last_drill: ResumeExecution | null
}

export interface EssaiRestauration {
  backup_id: string
  kind: string
  request_id: string | null
  restore_status: 'succeeded' | 'failed' | string
  restore_target: string | null
  target_container: string | null
  restore_started_at: string | null
  restore_finished_at: string | null
  restore_duration_seconds: number | null
  restore_source_sha256: string | null
  tables_verified: number | null
  rows_verified: number | null
  mismatches: number
  error: string | null
}

export type CibleRestauration = 'jetable' | 'cible' | 'en_place'

export interface DemandeContinuite {
  id: string
  kind: 'backup' | 'restore'
  target: CibleRestauration | null
  backup_id: string | null
  target_name: string | null
  reason: string
  requested_by: string
  requested_at: string
  command: string
  executed: boolean
  execution: ResumeExecution | null
}

export interface EtatContinuite {
  journal: { configured: boolean; readable: boolean; invalid_lines: number }
  backup_state: 'never' | 'ok' | 'stale' | 'last_failed'
  drill_state: 'never' | 'ok' | 'last_failed'
  rpo_target_minutes: number
  last_backup: SauvegardeDoIt | null
  last_success: SauvegardeDoIt | null
  last_drill: EssaiRestauration | null
  backups: SauvegardeDoIt[]
  drills: EssaiRestauration[]
  requests: DemandeContinuite[]
  backup_command_preview: string
}

export interface DemandeRestauration {
  backup_id: string
  target: CibleRestauration
  reason: string
  target_name?: string
  confirm_in_place?: boolean
  confirmation?: string
}

// --------------------------------------------------------------------- accès exceptionnels

export type StatutAcces = 'active' | 'expired' | 'revoked'

export interface SessionAcces {
  id: string
  actor_id: string
  actor_organisation_id: string | null
  target_organisation_id: string
  target_organisation_name?: string | null
  scope: 'read'
  reason: string
  duration_minutes: number
  started_at: string
  expires_at: string
  notified_count: number
  revoked_at: string | null
  revoked_by: string | null
  revoke_reason: string | null
  status: StatutAcces
  remaining_seconds: number
  mine: boolean
}

export interface ListeAcces {
  sessions: SessionAcces[]
  active_count: number
  duration_minutes: { min: number; max: number; default: number }
  reason_min_length: number
  scope: 'read'
}

export interface LectureOrganisation {
  session_id: string
  scope: 'read'
  expires_at: string
  rls_tenant: string
  organisation: { organisation_id: string; name: string; status: string | null; created_at: string | null } | null
  plan_code: string | null
  projects: { workspace_id: string; name: string; status: string | null; program_id: string | null }[]
  programs: { program_id: string; name: string; status: string | null }[]
  missions: { id: string; workspace_id: string; title: string; status: string; suspended_reason: string | null }[]
}

export interface AccesActifsOrganisation {
  organisation_id: string
  active: { id: string; actor_id: string; reason: string; started_at: string; expires_at: string }[]
}

// --------------------------------------------------------------------------------- palier

export interface EtatPalier {
  organisation_id: string
  plan_code: string
  limites: Record<string, number>
  changed_by: string | null
  changed_at: string | null
  reason: string | null
  usage: { resource_type: string; usage: number }[]
  ai_calls_used_today: number
}

export interface HistoriquePalier {
  organisation_id: string
  plan_code: string
  catalog: { plan_code: string; libelle: string; limites: string }[]
  history: { id: number; previous_plan_code: string | null; new_plan_code: string; changed_by: string; changed_at: string; reason: string }[]
}

export interface ReleveMensuel {
  organisation_id: string
  month: string
  plan_code: string
  metrics: { metric: string; total: number; max: number; jours: number }[]
}

// --------------------------------------------------------------------------- organisations

export interface OrganisationPlateforme {
  organisation_id: string
  name: string
  status: 'active' | 'suspended' | string
  created_at: string | null
  plan_code: string
  projects: number
  members: number
  live_missions: number
  tokens_month: number
  ai_calls_month: number
  last_status_change: string | null
  status_changed_at: string | null
  status_changed_by: string | null
  status_reason: string | null
  missions_policy: 'laisser_finir' | 'arreter' | null
}

export interface ListeOrganisations {
  organisations: OrganisationPlateforme[]
  missions_deadline_minutes: number
}

export interface ChangementStatut {
  organisation_id: string
  previous_status: string | null
  new_status: string
  changed_at: string
  reason: string
  missions_policy?: string
  missions_stopped?: number
}

// ------------------------------------------------------------------------- enrôlement

export interface Enrolement {
  id: string
  mode: 'link' | 'email'
  status: 'pending' | 'consumed' | 'revoked' | 'expired'
  expires_at: string
  /** Chemin d'activation, rendu UNE seule fois (mode lien) ; jamais relisible ensuite. */
  activation_path?: string | null
  shown_once?: boolean
  email?: string
  created_at?: string
  consumed_at?: string | null
  revoked_at?: string | null
}

export interface DemandeProvisionnement {
  name: string
  organisation_id?: string | undefined
  description?: string | undefined
  admin_email: string
  admin_first_name: string
  admin_last_name: string
}

export interface ResultatProvisionnement {
  organisation_id: string
  workspace_id: string
  admin_user_id: string
  admin_email: string
  role: string
  enrolment: Enrolement
  warning: string | null
}

export interface EtatActivation {
  status: 'valid' | 'consumed' | 'revoked' | 'expired'
  organisation_name: string | null
  email: string
  expires_at: string
  /** Lot USR : la même page sert l'administrateur enrôlé, le membre invité et la réinitialisation. */
  kind?: 'organisation_admin' | 'member_invitation' | 'access_reset'
  org_role?: string | null
  password_min_length?: number
  password_min_classes?: number
  mfa_required?: boolean
}

// ------------------------------------------------------------------------------- journal

export interface LigneJournal {
  source: 'audit' | 'event'
  ref: number
  occurred_at: string
  actor: string
  action: string
  resource_type: string
  resource_id: string
  workspace_id?: string | null
  organisation_id?: string
  detail: string | null
}

export interface PageJournal {
  items: LigneJournal[]
  total: number
  limit: number
  offset: number
}

export interface FiltresJournal {
  workspace_id?: string | undefined
  organisation_id?: string | undefined
  actor?: string | undefined
  action?: string | undefined
  exclude?: string | undefined
  resource_type?: string | undefined
  resource_id?: string | undefined
  from?: string | undefined
  to?: string | undefined
  source?: 'all' | 'audit' | 'events' | undefined
  limit?: number | undefined
  offset?: number | undefined
}

/** Bruit d'arrêt automatique d'environnement (une ligne toutes les 3 s, constaté) : filtrable. */
export const BRUIT_ARRET_AUTOMATIQUE = 'environment_auto_stop_failed'

function requete(f: FiltresJournal, format?: 'csv'): string {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== null && String(v).trim() !== '') q.set(k, String(v).trim())
  }
  if (format) q.set('format', format)
  const s = q.toString()
  return s ? `?${s}` : ''
}

/** Télécharge un CSV de la passerelle avec le jeton de session (même geste que les rapports de campagne). */
async function telechargerCsv(chemin: string, nom: string): Promise<void> {
  const token = getToken()
  const response = await fetch(`${BASE}${chemin}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
  if (!response.ok) throw new ApiError(response.status, httpMessage(response.status), await response.text().catch(() => undefined))
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const lien = document.createElement('a')
  lien.href = url
  lien.download = nom
  document.body.appendChild(lien)
  lien.click()
  lien.remove()
  URL.revokeObjectURL(url)
}

/** Clé de cache de la liste des organisations (Plateforme › Organisations). */
export const CLE_ORGANISATIONS = ['plateforme', 'organisations', 'liste'] as const

const enc = encodeURIComponent

export const plateformeApi = {
  continuite: () => request<EtatContinuite>('/admin/continuity'),
  demanderSauvegarde: (reason: string) =>
    request<DemandeContinuite>('/admin/continuity/backup-requests', { method: 'POST', body: JSON.stringify({ reason }) }),
  demanderRestauration: (corps: DemandeRestauration) =>
    request<DemandeContinuite>('/admin/continuity/restore-requests', { method: 'POST', body: JSON.stringify(corps) }),

  accesExceptionnels: () => request<ListeAcces>('/admin/exceptional-access'),
  ouvrirAcces: (organisationId: string, reason: string, durationMinutes: number) =>
    request<SessionAcces>('/admin/exceptional-access', {
      method: 'POST', body: JSON.stringify({ organisation_id: organisationId, reason, duration_minutes: durationMinutes }),
    }),
  revoquerAcces: (id: string, reason?: string) =>
    request<SessionAcces>(`/admin/exceptional-access/${enc(id)}/revocation`, {
      method: 'POST', body: JSON.stringify({ reason: reason ?? null }),
    }),
  lireOrganisation: (id: string) => request<LectureOrganisation>(`/admin/exceptional-access/${enc(id)}/organisation`),
  accesActifsOrganisation: (organisationId: string) =>
    request<AccesActifsOrganisation>(`/organisations/${enc(organisationId)}/exceptional-access`),

  palier: (organisationId: string) => request<EtatPalier>(`/organisations/${enc(organisationId)}/plan`),
  historiquePalier: (organisationId: string) => request<HistoriquePalier>(`/admin/organisations/${enc(organisationId)}/plan-history`),
  changerPalier: (organisationId: string, planCode: string, reason: string) =>
    request<EtatPalier>(`/admin/organisations/${enc(organisationId)}/plan`, {
      method: 'PUT', body: JSON.stringify({ plan_code: planCode, reason }),
    }),
  releve: (organisationId: string, mois: string) =>
    request<ReleveMensuel>(`/admin/organisations/${enc(organisationId)}/usage-statement?month=${enc(mois)}`),

  organisations: () => request<ListeOrganisations>('/admin/organisations'),
  suspendre: (organisationId: string, reason: string, missions: 'laisser_finir' | 'arreter') =>
    request<ChangementStatut>(`/admin/organisations/${enc(organisationId)}/suspension`, {
      method: 'POST', body: JSON.stringify({ reason, missions }),
    }),
  reactiver: (organisationId: string, reason: string) =>
    request<ChangementStatut>(`/admin/organisations/${enc(organisationId)}/reactivation`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),

  provisionner: (d: DemandeProvisionnement) =>
    request<ResultatProvisionnement>('/admin/organisations/provisioning', { method: 'POST', body: JSON.stringify(d) }),
  enrolements: (organisationId: string) =>
    request<{ organisation_id: string; enrolments: Enrolement[] }>(`/admin/organisations/${enc(organisationId)}/enrolments`),
  nouveauLien: (organisationId: string) =>
    request<Enrolement>(`/admin/organisations/${enc(organisationId)}/enrolments`, { method: 'POST', body: '{}' }),
  revoquerLien: (organisationId: string, enrolmentId: string) =>
    request<Enrolement>(`/admin/organisations/${enc(organisationId)}/enrolments/${enc(enrolmentId)}/revocation`, { method: 'POST', body: '{}' }),
  /** Publics : le jeton voyage dans le CORPS, jamais dans l'URL d'une requête à la passerelle. */
  etatActivation: (token: string) =>
    request<EtatActivation>('/public/enrolment/status', { method: 'POST', body: JSON.stringify({ token }) }),
  activer: (token: string, password: string) =>
    request<{ status: 'activated'; username: string; organisation_name: string | null }>('/public/enrolment/activate', {
      method: 'POST', body: JSON.stringify({ token, password }),
    }),

  journal: (f: FiltresJournal) => request<PageJournal>(`/audit/journal${requete(f)}`),
  exporterJournal: (f: FiltresJournal) =>
    telechargerCsv(`/audit/journal${requete({ ...f, limit: undefined, offset: undefined }, 'csv')}`, 'journal-audit.csv'),
  journalPlateforme: (f: FiltresJournal) => request<PageJournal>(`/admin/platform-journal${requete(f)}`),
  exporterJournalPlateforme: (f: FiltresJournal) =>
    telechargerCsv(`/admin/platform-journal${requete({ ...f, limit: undefined, offset: undefined }, 'csv')}`, 'journal-plateforme.csv'),
}
